import { useState, useCallback, useMemo } from 'react';
import { Title, Text, Stack, Skeleton, Paper, SimpleGrid } from '@mantine/core';
import type { DataLibrarySurveyRow } from '../../types';
import { useCoachDashboardData, SATISFACTION_ORDER, COACH_SATISFACTION_ORDER } from './useCoachDashboardData';
import { CoachDashboardFilters } from './CoachDashboardFilters';
import { RecordCountByCoachChart } from './RecordCountByCoachChart';
import { SatisfactionPieChart } from './SatisfactionPieChart';
import { MidpointEndpointChart } from './MidpointEndpointChart';
import { CoachDashboardDrawer } from './CoachDashboardDrawer';

interface DrawerState {
  opened: boolean;
  chartTitle: string;
  segmentLabel: string;
  rows: DataLibrarySurveyRow[];
  valueField: 'overall_satisfaction' | 'coach_satisfaction';
}

export function CoachDashboardLayout() {
  const {
    filteredRows,
    loading,
    filters,
    setFilters,
    resetFilters,
    hasActiveFilters,
    coachOptions,
    satisfactionOptions,
    cohortOptions,
  } = useCoachDashboardData();

  const [drawer, setDrawer] = useState<DrawerState>({
    opened: false,
    chartTitle: '',
    segmentLabel: '',
    rows: [],
    valueField: 'overall_satisfaction',
  });

  const closeDrawer = useCallback(() => {
    setDrawer(prev => ({ ...prev, opened: false }));
  }, []);

  // Drilldown handlers for each chart section
  const handleCoachBarClick = useCallback(
    (coach: string, rating: string) => {
      const drillRows = filteredRows.filter(
        r => r.coach_email === coach && r.overall_satisfaction === rating,
      );
      setDrawer({
        opened: true,
        chartTitle: 'Record count by coach',
        segmentLabel: `Coach: ${coach}, Overall satisfaction: ${rating}`,
        rows: drillRows,
        valueField: 'overall_satisfaction',
      });
    },
    [filteredRows],
  );

  const handleOverallSatisfactionClick = useCallback(
    (rating: string) => {
      const drillRows = filteredRows.filter(r => r.overall_satisfaction === rating);
      setDrawer({
        opened: true,
        chartTitle: 'Overall Satisfaction',
        segmentLabel: `Overall satisfaction is ${rating}`,
        rows: drillRows,
        valueField: 'overall_satisfaction',
      });
    },
    [filteredRows],
  );

  const handleCoachSatisfactionClick = useCallback(
    (rating: string) => {
      const drillRows = filteredRows.filter(r => r.coach_satisfaction === rating);
      setDrawer({
        opened: true,
        chartTitle: 'Coach Satisfaction',
        segmentLabel: `Coach satisfaction is ${rating}`,
        rows: drillRows,
        valueField: 'coach_satisfaction',
      });
    },
    [filteredRows],
  );

  const handleMidEndOverallClick = useCallback(
    (cohort: string, stage: 'Midpoint' | 'Endpoint', rating: string) => {
      const drillRows = filteredRows.filter(
        r => r.cohort_name === cohort && r.survey_stage === stage && r.overall_satisfaction === rating,
      );
      setDrawer({
        opened: true,
        chartTitle: `${stage} — Overall Satisfaction`,
        segmentLabel: `Cohort: ${cohort}, Stage: ${stage}, Overall satisfaction: ${rating}`,
        rows: drillRows,
        valueField: 'overall_satisfaction',
      });
    },
    [filteredRows],
  );

  const handleMidEndCoachClick = useCallback(
    (cohort: string, stage: 'Midpoint' | 'Endpoint', rating: string) => {
      const drillRows = filteredRows.filter(
        r => r.cohort_name === cohort && r.survey_stage === stage && r.coach_satisfaction === rating,
      );
      setDrawer({
        opened: true,
        chartTitle: `${stage} — Coach Satisfaction`,
        segmentLabel: `Cohort: ${cohort}, Stage: ${stage}, Coach satisfaction: ${rating}`,
        rows: drillRows,
        valueField: 'coach_satisfaction',
      });
    },
    [filteredRows],
  );

  // Rows that have coach_satisfaction data (for Section 4)
  const coachSatisfactionRows = useMemo(
    () => filteredRows.filter(r => r.coach_satisfaction !== null),
    [filteredRows],
  );

  if (loading) {
    return (
      <Stack gap="lg">
        <div>
          <Title order={2}>Coach Dashboard</Title>
          <Text c="dimmed" size="sm">Participant survey response analytics by coach</Text>
        </div>
        <Skeleton height={40} />
        <Skeleton height={350} radius="md" />
        <SimpleGrid cols={2}>
          <Skeleton height={300} radius="md" />
          <Skeleton height={300} radius="md" />
        </SimpleGrid>
        <Skeleton height={300} radius="md" />
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <div>
        <Title order={2}>Coach Dashboard</Title>
        <Text c="dimmed" size="sm">
          Participant survey response analytics by coach
        </Text>
      </div>

      {/* Global Filters */}
      <CoachDashboardFilters
        filters={filters}
        onFiltersChange={setFilters}
        onReset={resetFilters}
        hasActiveFilters={hasActiveFilters}
        coachOptions={coachOptions}
        satisfactionOptions={satisfactionOptions}
        cohortOptions={cohortOptions}
      />

      {/* Empty state for all charts */}
      {filteredRows.length === 0 ? (
        <Paper p="xl" radius="md" withBorder>
          <Text c="dimmed" ta="center" py="xl" size="lg">
            No survey responses match these filters
          </Text>
        </Paper>
      ) : (
        <>
          {/* Section 1: Record count by coach */}
          <RecordCountByCoachChart
            rows={filteredRows}
            onSegmentClick={handleCoachBarClick}
          />

          {/* Section 2: Overall Satisfaction + Section 3: Midpoint vs Endpoint */}
          <SatisfactionPieChart
            title="Overall Satisfaction"
            rows={filteredRows}
            field="overall_satisfaction"
            order={SATISFACTION_ORDER}
            onSegmentClick={handleOverallSatisfactionClick}
          />

          <MidpointEndpointChart
            title="Overall Satisfaction"
            rows={filteredRows}
            field="overall_satisfaction"
            onSegmentClick={handleMidEndOverallClick}
          />

          {/* Section 4: Coach Satisfaction */}
          <SatisfactionPieChart
            title="Coach Satisfaction"
            rows={coachSatisfactionRows}
            field="coach_satisfaction"
            order={COACH_SATISFACTION_ORDER}
            onSegmentClick={handleCoachSatisfactionClick}
          />

          <MidpointEndpointChart
            title="Coach Satisfaction"
            rows={coachSatisfactionRows}
            field="coach_satisfaction"
            onSegmentClick={handleMidEndCoachClick}
          />
        </>
      )}

      {/* Drilldown Drawer */}
      <CoachDashboardDrawer
        opened={drawer.opened}
        onClose={closeDrawer}
        chartTitle={drawer.chartTitle}
        segmentLabel={drawer.segmentLabel}
        rows={drawer.rows}
        valueField={drawer.valueField}
      />
    </Stack>
  );
}
